import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, Users, Rocket, ChartBar } from "lucide-react";
import posthog from "posthog-js";

interface BookStrategySessionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBook?: () => void;
}

export function BookStrategySessionModal({
  open,
  onOpenChange,
  onBook,
}: BookStrategySessionModalProps) {
  const handleClose = () => {
    onOpenChange(false);
  };

  const handleBookSession = () => {
    if (onBook) {
      onBook();
    } else {
      // Fallback if onBook is not provided
      posthog.capture("strategy_session_booked", {
        source: "modal",
      });
      window.open("https://cal.com/aicreator/30min", "_blank");
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Let's Build Your Content Strategy Together
          </DialogTitle>
          <DialogDescription className="text-base mt-2">
            Book a free 30-minute strategy session with our content experts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <ChartBar className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium">Analyze Your Website</h4>
                <p className="text-sm text-muted-foreground">
                  We'll review your current content and identify opportunities
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium">Understand Your Competitors</h4>
                <p className="text-sm text-muted-foreground">
                  Discover what content strategies work in your industry
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Rocket className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium">Create Content Pillars</h4>
                <p className="text-sm text-muted-foreground">
                  Define topics that resonate with your audience
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium">Get a Platform Demo</h4>
                <p className="text-sm text-muted-foreground">
                  See how Creator can accelerate your content production
                </p>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground text-center">
              <span className="font-medium">100% Free</span> • No obligations •
              30 minutes of actionable insights
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            Maybe Later
          </Button>
          <Button onClick={handleBookSession} className="gap-2">
            <Calendar className="h-4 w-4" />
            Book Strategy Session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}